import React, { useState } from "react";
import {
  SearchHint,
  updateSearchHints,
  useMarkSearchHintAsViewedMutation,
  UserState,
} from "../../hooks/slices/userSlice";
import { DateUtil } from "../../utils/date";
import { RaiseUser, UserLanguage } from "../../hooks/raiseUser";
import { Language } from "../../consts/languages";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { useDispatch } from "react-redux";
import moment, { Moment } from "moment";
import {CloseIcon, InfoIcon} from "../icons";
import { FADE_IN_TOP } from "../../consts/FramerMotionVariants";
import { AnimatePresence, motion } from "framer-motion";

export const SearchHintBanner: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language: Language = UserLanguage(i18n.language);
  const dispatch = useDispatch();
  const user: UserState | undefined = RaiseUser().user;
  const [searchHints, setSearchHints] = useState<SearchHint[]>(user?.searchHints ?? []);
  const [markSearchHintAsViewed, { data, isLoading, isSuccess, isError, error }] = useMarkSearchHintAsViewedMutation();
  //When a user has a limited amount of opportunities per update mail, they cannot use the filters in opportunities
  const searchHint: SearchHint | undefined = findSearchHint(searchHints);
  const close = (hint: SearchHint) => {
    markSearchHintAsViewed(hint);
    let viewedHint: SearchHint = {
      id: hint.id,
      dutchHint: hint.dutchHint,
      frenchHint: hint.frenchHint,
      englishHint: hint.englishHint,
      priority: hint.priority,
      viewedAt: DateUtil.getDateTimeString(moment()),
    };
    let newSearchHints: SearchHint[] = [...searchHints];
    newSearchHints = newSearchHints?.filter((h) => h.id !== hint.id);
    newSearchHints.push(viewedHint);
    dispatch(updateSearchHints(newSearchHints));
    setSearchHints(newSearchHints);
  };
  return (
    <AnimatePresence>
      {searchHint && (
        <motion.div
          className={styles["search-hint"]}
          variants={FADE_IN_TOP}
          initial={"hidden"}
          animate={"visible"}
          exit={"hidden"}
        >
          <span className={styles["info-part"]}>
            <span className={styles["info-icon"]}><InfoIcon/></span>
            <span className={styles["info-text"]}>{translate(searchHint, language)}</span>
          </span>
          <span className={styles["close-button"]} onClick={() => close(searchHint)} title={t("interactions.close")}>
            <CloseIcon />
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

function translate(searchHint: SearchHint, language: Language): string {
  if (language === Language.NL) return searchHint.dutchHint;
  else if (language === Language.FR) return searchHint.frenchHint;
  else return searchHint.englishHint;
}

function findSearchHint(searchHints: SearchHint[]): SearchHint | undefined {
  let hintsNotViewed: SearchHint[] = searchHints.filter((hint) => hint.viewedAt == null);
  if (hintsNotViewed.length > 0) {
    let views: Moment[] = searchHints
      .filter((h) => h.viewedAt != null)
      .map((h) => DateUtil.convertDateTimeStringToDateString(h.viewedAt) ?? "")
      .map((date) => DateUtil.getDateMoment(date))
      .sort();
    if (views.length === 0 || views[views.length - 1].isBefore(DateUtil.today(), "day")) {
      return hintsNotViewed.sort((a, b) => (a.priority > b.priority ? 1 : b.priority > a.priority ? -1 : 0))[0];
    }
  }
  return undefined;
}

//Todo deprecated old search hints

// function getSearchHint(searchInput: TenderSearchInput,
//                        searchResponse: SearchResponse,
//                        currentPlan: PlanDto | undefined,
//                        client: Client | undefined,
//                        user: UserState | undefined,
//                        opportunitiesLimit: number | undefined,
//                        language: Language,
//                        t: TFunction): SearchHintMessage | undefined {
//     if (currentPlan === undefined || client === undefined || user === undefined) return {parts: []};
//     let message: SearchHintMessage | undefined = getTrialSearchHint(currentPlan, client, t);
//     if (message == null && opportunitiesLimit) return getOpportunitiesSearchHint(opportunitiesLimit, t);
//     if (message == null) message = getTooFewResultsSearchHint(searchInput, searchResponse, currentPlan, t);
//     if (message == null) message = getCustomUserSearchHints(user, language);
//     return message;
// }
//
// function getTrialSearchHint(currentPlan: PlanDto, client: Client, t: TFunction): SearchHintMessage | undefined {
//     if(currentPlan.type === "TRIAL" && client.clientType !== "GOVERNMENT"){
//         return {parts:[{text: t("searchFilterTags.trial_message_part_one")},
//                 {text: t("searchFilterTags.trial_message_part_two"), url: "/account?tab=feedback"},
//                 {text: t("searchFilterTags.trial_message_part_three")},
//                 {text: t("searchFilterTags.trial_message_part_four")},
//                 {text: t("searchFilterTags.trial_message_part_five"), url: "/account?tab=help"}
//             ]}
//     } else return undefined;
// }
//
// function getOpportunitiesSearchHint(opportunitiesLimit: number, t: TFunction): SearchHintMessage{
//     return {parts:[{text: t("searchFilterTags.limited_results_message_part_one") +
//                     opportunitiesLimit +
//                     t("searchFilterTags.limited_results_message_part_two")}]}
//
// }
//
// function getTooFewResultsSearchHint(query: TenderSearchInput, results: SearchResponse, currentPlan: PlanDto, t: TFunction): SearchHintMessage | undefined {
//     let sideBarHasPublicationDateFilter = query.metadata.searchType !== SearchType.SHORTLIST;
//     if (results.totalHits < 2 && sideBarHasPublicationDateFilter){
//         let earliestStartDate: Moment | undefined = DateFilterUtil.getEarliestStartDateForUser(currentPlan.features);
//         let earliestStartString = earliestStartDate ? earliestStartDate.format("DD-MM-YYYY") : "...";
//         let start = query.mainInput.publicationStartDate;
//         let end = query.mainInput.publicationEndDate;
//         let startString = start ? DateUtil.getDateMoment(start).format("DD-MM-YYYY") : "...";
//         let endString = end ? DateUtil.getDateMoment(end).format("DD-MM-YYYY") : "...";
//         if (start != null && end != null && earliestStartString !== startString){
//             return {parts:[{text: t("searchFilterTags.hints_date_message_part_one")
//                         + startString + t("searchFilterTags.hints_date_message_part_two") + endString +
//                         t("searchFilterTags.hints_date_message_part_three")}]};
//         }
//     } else return undefined;
// }
